import { Navigate, Outlet, useLocation, type Location } from "react-router";

import { useSession } from "@/src/contexts/AuthContext";
import { ROUTES } from "@/src/routes/paths";

/** Ce qu'une redirection vers `/sign-in` transporte pour pouvoir revenir. */
type RedirectState = { from?: Location } | null;

/**
 * Routes réservées aux administrateurs connectés.
 *
 * C'est une **route de mise en page sans chemin** : toutes les routes privées
 * sont ses enfants, donc la garde s'applique par construction. Ne la
 * court-circuite pas en déclarant un écran sensible à la racine — c'est
 * exactement le trou de `wargame-app`, où seule la navigation par onglets est
 * gardée et où tous les autres écrans restent atteignables par lien direct.
 *
 * Aucune vérification de rôle ici : `useSignIn` refuse déjà d'ouvrir une session
 * pour un compte sans le rôle `admin`, donc une session existante est
 * nécessairement celle d'un administrateur.
 */
export function RequireAuth() {
  const { session } = useSession();
  const location = useLocation();

  if (!session) {
    // `replace` : la page refusée ne doit pas rester dans l'historique, sinon
    // le bouton « précédent » y ramène après connexion.
    // `state.from` : mémorise la destination pour y revenir juste après.
    //
    // L'objet littéral est intentionnel — `Navigate` ne rend rien et redirige
    // dans un effet, il n'y a aucune mémoïsation à préserver ici.
    // oxlint-disable-next-line react-perf/jsx-no-new-object-as-prop
    return <Navigate replace state={{ from: location }} to={ROUTES.signIn} />;
  }

  return <Outlet />;
}

/**
 * Routes interdites à qui est **déjà** connecté (la page de connexion).
 *
 * Renvoie sur la page initialement demandée quand il y en avait une, sinon sur
 * le tableau de bord. C'est aussi ce qui fait naviguer l'application après une
 * connexion réussie : `useSignIn` se contente d'écrire la session, la
 * redirection en découle.
 */
export function PublicOnly() {
  const { session } = useSession();
  const location = useLocation();

  if (session) {
    const from = (location.state as RedirectState)?.from?.pathname;
    return <Navigate replace to={from ?? ROUTES.dashboard} />;
  }

  return <Outlet />;
}
